/**
 * PhysicsTypes - physics type definitions and configuration interfaces. Contains motion
 * types, collision mesh types, voxel/heightfield descriptors and component physics options.
 */

import type { DefaultCollisionProfile } from '../systems/collision/CollisionTypes.js';
import type * as THREE from 'three';


export enum PhysicsMotionType {
  Static = 'static',                                   // infinite mass and cannot move
  KinematicVelocityBased = 'kinematicVelocityBased',   // Indicates that the body velocity must not be altered by the physics engine.
  KinematicPositionBased = 'kinematicPositionBased',   // Indicates that the body position must not be altered by the physics engine.
  Dynamic = 'dynamic',                                 // simulated according to applied forces
}

/**
 * Type of collision mesh to use for this component
 */
export enum CollisionMeshType {
  // Simple bounding box collision, fast but not accurate
  BoundingBox = 'boundingBox',
  // Simple bounding sphere collision, fast but not accurate
  BoundingSphere = 'boundingSphere',
  // Convex hull collision, accurate but slower
  ConvexHull = 'convexHull',
  // Trimesh collision, accurate and slowest
  Trimesh = 'trimesh',
  // Voxelized mesh collision
  Voxels = 'voxels',
}

export interface VoxelCollisionDesc {
  // Size of each voxel in the grid, default to (1,1,1)
  voxelSize?: THREE.Vector3;
  // Defines the set of voxels. If this is a `Int32Array` then
  // each voxel is defined from its (signed) grid coordinates,
  // with 3 (resp 2) contiguous integers per voxel in 3D (resp 2D).
  // If this is a `Float32Array`, each voxel will be such that
  // they contain at least one point from this array (where each
  // point is defined from 3 (resp 2) contiguous numbers per point
  // in 3D (resp 2D).
  coordinates?: Float32Array | Int32Array;
  // Optional offset to apply to the voxel grid
  offset?: THREE.Vector3;
}

/**
 * Collision descriptor for a terrain-like heightfield sampled on an XZ grid.
 * Heights are expected to be in row-major order: index = row * widthPixels + col.
 */
export interface HeightfieldCollisionDesc {
  /** Full width of the terrain in local/world units before component scaling */
  width: number;
  /** Full depth of the terrain in local/world units before component scaling */
  depth: number;
  /** Number of samples along the width/X axis */
  widthPixels: number;
  /** Number of samples along the depth/Z axis */
  depthPixels: number;
  /** Height samples, row-major. Float32Array recommended for performance. */
  heights: Float32Array | number[];
  /**
   * Maximum height amplitude used for normalized centered data.
   * Required when `heightEncoding` is `normalizedCentered`.
   */
  maxHeight?: number;
  /**
   * - `worldY`: height values are already world/local Y units.
   * - `normalizedCentered`: converts each sample as `n * maxHeight - maxHeight / 2`.
   */
  heightEncoding?: 'worldY' | 'normalizedCentered';
  /**
   * Local rotation correction for the heightfield collider relative to the rigid body.
   *
   * The heightfield's native orientation is an XZ plane (Y-up). Use this when the
   * visual mesh has a different orientation to align the collider with the visual.
   */
  colliderRotationCorrection?: THREE.Quaternion;
}


/**
 * Unified interface containing collision type-specific configuration
 * Add new fields here as new collision types require specific configuration
 */
export interface CollisionTypeConfig {
  /** Configuration specific to voxel collision type */
  voxels?: VoxelCollisionDesc;
  /** Configuration specific to heightfield collision type */
  heightfield?: HeightfieldCollisionDesc;
}

/**
 * Physics motion type for components with physics enabled
 */
export interface ComponentPhysicsOptions {
  /** Whether physics is enabled for this component */
  enabled?: boolean;
  /** The type of motion for this physics body */
  motionType?: PhysicsMotionType;
  /** The density of the physics body */
  density?: number;
  /**
   * Whether this component should be ignored for detecting user input interactions (like clicks)
   * When true, the component will still participate in physics simulation but will be ignored for input detection
   */
  ignoreForInputCollision?: boolean;
  /**
   * Whether this component should generate collision events
   * When true, the component will generate collision events when it collides with another physics-enabled component
   */
  generateCollisionEvents?: boolean;
  /**
   * The gravity scale for this component
   * For trigger components using dynamic motion type, set this to 0 to disable gravity
   */
  gravityScale?: number;

  /**
   * The type of collision mesh to use for this component, only relevant for non-primitive meshes.
   * - BoundingBox: Simple bounding box, fastest but least accurate
   * - ConvexHull: Convex hull, more accurate but slower
   * - Trimesh: Triangle mesh, most accurate but slowest (only for static objects)
   */
  collisionMeshType?: CollisionMeshType;

  /**
   * Collision type-specific configuration data
   * Use this to provide additional configuration for specific collision types:
   * - For voxel collision: use collisionData.voxels to configure voxel size, coordinates, etc.
   * - For heightfield collision: use collisionData.heightfield to provide sampled terrain data.
   *
   * Example:
   * ```typescript
   * physicsOptions: {
   *   collisionMeshType: CollisionMeshType.Voxels,
   *   collisionData: {
   *     voxels: {
   *       voxelSize: new THREE.Vector3(1, 1, 1),
   *       coordinates: new Int32Array([0, 0, 0, 0, 1, 0]),
   *     }
   *   }
   * }
   * ```
   */
  collisionData?: CollisionTypeConfig;

  /**
   * Whether to enable continuous collision detection for this component
   */
  enableCCD?: boolean;

  /**
   * Collision profile to use for this component
   */
  collisionProfile?: DefaultCollisionProfile | string;
}